import {dedupingMixin} from '@polymer/polymer/lib/utils/mixin';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {microTask} from '@polymer/polymer/lib/utils/async.js';
import {TooltipBehavior} from '../behaviors/tooltip-behavior.js';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';
import {backgroundColorValue, desiredTextColor, foregroundColorValue} from '../../javascripts/modules/colors.js';

/**
 * @polymer
 * @mixinFunction
 **/
export const InputBehavior = dedupingMixin((superClass) => class extends ResizeObserverBehavior(TooltipBehavior(superClass)) {
  static get properties() {
    return {
      _clientWidth: {type: Number, observer: '_clientWidthChanged'},
      _currentValueInitialized: {type: Boolean},
      active: {type: Boolean, observer: '_activeChanged'},
      backgroundColor: {type: String, observer: '_backgroundColorChanged', scopevar: true},
      caption: {type: String, scopevar: true},
      chrome: {type: Boolean, value: true},
      color: {type: String, observer: '_colorChanged', scopevar: true},
      currentValue: {type: String, notify: true, observer: '_currentValueChanged'},
      currentValueLength: Number,
      defaultErrorMessage: {type: String, value: 'There is an error in this field.'},
      disabled: {type: Boolean, observer: '_disabledChanged'},
      displayValue: {
        type: String,
        observer: '_displayValueChanged',
        scopevar: {options: {peek: false, confine: false}}
      },
      errorMessage: {type: String, value: 'There is an error in this field.'},
      fieldInputOldValue: String,
      fireKeliAccepted: Boolean,
      focused: {type: Boolean, observer: '_focusedChanged'},
      focusElement: {type: Boolean, observer: '_focusElementChanged', scopevar: 'focus'},
      formattedShortcutKey: {type: String, computed: '_computeFormattedShortcutKey(shortcutKey, readOnly)'},
      helpText: {type: String, scopevar: 'help', observer: '_helpTextChanged'},
      hidden: Boolean,
      hotkey: {type: String, scopevar: true, observer: '_hotkeyChanged'},
      icon: {type: String, scopevar: true},
      id: String,
      inputFormatRequired: {type: Boolean, value: true},
      invalid: {type: Boolean},
      masked: {type: Boolean, observer: '_maskedChanged'},
      maxLength: {type: Number, observer: '_maxLengthChanged', scopevar: true},
      needsDataLoaded: {type: Boolean, value: true},
      preventKeliAcceptOnChangeThenRevert: {type: Boolean, value: false},
      preventPanelEvents: Boolean,
      sendEditRowEvents: Boolean,
      readOnly: {type: Boolean, observer: '_readOnlyChanged', scopevar: 'ro'},
      selectOnFocus: {type: Boolean, value: true},
      shortcutKey: {type: String},
      showOverflowTooltip: {type: Boolean},
      skip: {type: Boolean, scopevar: true, observer: '_skipChanged'},
      textCasing: {type: String, observer: '_textCasingChanged', scopevar: true},
      tooltipText: {
        type: String,
        computed: '_computeTooltipText(currentValue, _clientWidth, showOverflowTooltip, focused, invalid, _currentValueInitialized)'
      },
      validateOnBlur: Boolean,
      validityState: {type: Object, observer: '_validityStateChanged', scopevar: true}
    };
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliUserChange', 'keliFocus', 'keliBlur', 'keliAccepted', 'keliIconAction', 'keliDoubleClick']);
  }

  connectedCallback() {
    super.connectedCallback();

    this.classList.add('input-field');
    if (this.$.input && !this.$.input.hasAttribute('autocomplete')) {
      this.$.input.setAttribute('autocomplete', 'off');
    }

    this.addEventListener('focusElement', this._handleFocusElement);
    this.addEventListener('click', this._handleClick);
    this.addEventListener('keli-input-icon-activated', this._handleInputIconActivated);

    if (!this.preventPanelEvents) {
      this.dispatchEvent(new CustomEvent('panelElementCreated', {
        bubbles: true,
        composed: true,
        detail: {element: this}
      }));
    }

    if (this.showOverflowTooltip) {
      this._createInputTooltip();
    }

    if (this.$.input) {
      this._boundHandleInput = this._handleInput.bind(this);
      this._boundHandleFocus = this._handleFocus.bind(this);
      this._boundHandleBlur = this._handleBlur.bind(this);
      this._boundHandleKeyDown = this._handleKeyDown.bind(this);
      this._boundHandleKeyPress = this._handleKeyPress.bind(this);
      this._boundHandleMouseDown = this._handleMouseDown.bind(this);
      this._boundHandleMouseUp = this._handleMouseUp.bind(this);
      this._boundHandleDoubleClick = this._handleDoubleClick.bind(this);
      this.$.input.addEventListener('input', this._boundHandleInput);
      this.$.input.addEventListener('focus', this._boundHandleFocus);
      this.$.input.addEventListener('blur', this._boundHandleBlur);
      this.$.input.addEventListener('keydown', this._boundHandleKeyDown);
      this.$.input.addEventListener('keypress', this._boundHandleKeyPress);
      this.$.input.addEventListener('down', this._boundHandleMouseDown);
      this.$.input.addEventListener('up', this._boundHandleMouseUp);
      this.$.input.addEventListener('dblclick', this._boundHandleDoubleClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusElement', this._handleFocusElement);
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keli-input-icon-activated', this._handleInputIconActivated);

    if (this.$.input) {
      this.$.input.removeEventListener('input', this._boundHandleInput);
      this.$.input.removeEventListener('focus', this._boundHandleFocus);
      this.$.input.removeEventListener('blur', this._boundHandleBlur);
      this.$.input.removeEventListener('keydown', this._boundHandleKeyDown);
      this.$.input.removeEventListener('keypress', this._boundHandleKeyPress);
      this.$.input.removeEventListener('down', this._boundHandleMouseDown);
      this.$.input.removeEventListener('up', this._boundHandleMouseUp);
      this.$.input.removeEventListener('dblclick', this._boundHandleDoubleClick);
    }
  }

  _createInputTooltip() {
    return this.addTooltip(this, 'tooltipText', {
      trigger: 'mouseenter focus',
      triggerTarget: this.$.input,
      hideOnClick: false,
      placement: 'bottom',
      touch: false,
      offset: [0, -5]
    });
  }

  _computeFormattedShortcutKey(shortcutKey, readOnly) {
    if (KeliInterface.isTouchDevice) {
      return;
    }
    if (readOnly) {
      return;
    }
    if (shortcutKey) {
      return formatHotkeyAsText(shortcutKey);
    }
  }

  _computeTooltipText(currentValue, _clientWidth, showOverflowTooltip, focused, invalid, _currentValueInitialized) {
    if (!this.chrome && !invalid) {
      return;
    }
    if (!showOverflowTooltip || (!this.chrome && !invalid)) {
      return;
    }

    let tooltipText;
    if (showOverflowTooltip) {
      if (invalid && !this.chrome) {
        tooltipText = this.$.input.validationMessage || this.errorMessage || this.defaultErrorMessage;
      } else if (currentValue && this.$.input && this.$.input.scrollWidth > this.$.input.clientWidth) {
        tooltipText = currentValue;
      }
    }

    let tooltip = this.getTooltipInstance(this);
    if (!tooltip && tooltipText) {
      tooltip = this._createInputTooltip();
    }
    if (tooltip) {
      if (tooltipText && focused) {
        tooltip.show();
      } else {
        tooltip.hide();
      }
    }
    return tooltipText;
  }

  handleElementResized(element, contentRect) {
    this._clientWidth = contentRect.width;
  }

  // Event handlers
  _handleFocusElement(event) {
    event.stopPropagation();
    if (KeliInterface.isTouchDevice) {
      return;
    }
    this._focusInput(event);
  }

  _handleClick(event) {
    if (!this.disabled) {
      this._focusInput(event);
    }
  }

  _handleInputIconActivated(event) {
    this.dispatchEvent(new CustomEvent('keliIconAction', {detail: {element: this, event: event.detail.originalEvent}}));
  }

  _handleMouseDown() {
    if (!this.focused && this.selectOnFocus && !this.disabled) {
      this.waitingForMouseUpAfterFocus = true;
    }
  }

  _handleMouseUp() {
    if (this.waitingForMouseUpAfterFocus) {
      delete this.waitingForMouseUpAfterFocus;
      microTask.run(() => {
        this.$.input.select();
      });
    }
  }

  _handleInput() {
    if (!this._executeOverride('handleInput', arguments)) {
      return;
    }
    this.fireKeliAccepted = true;
    if (!this.inputFormatRequired && (!this.$.input.validity || this.$.input.validity.valid !== false)) {
      this.currentValue = this.$.input.value;
    }
    if (this.fieldInputOldValue !== this.currentValue) {
      this.dispatchEvent(new CustomEvent('keliUserChange', {
        bubbles: true,
        composed: true,
        detail: {element: this, oldValue: this.fieldInputOldValue, newValue: this.currentValue}
      }));
      this.fieldInputOldValue = this.currentValue;
    }
  }

  _handleFocus(event) {
    if (!this._executeOverride('handleFocus', arguments)) {
      return;
    }
    this._focusInput(event);
  }

  _handleBlur(event) {
    const response = this._executeOverride('handleBlur', arguments);

    if (this.focused) {
      this.dispatchEvent(new CustomEvent('keliBlur', {
        bubbles: true,
        composed: true,
        detail: {element: this, event}
      }));

      if (response) {
        this._keliAccept(event);
      }

      this.focused = false;

      if (this.validateOnBlur) {
        this._validate();
      }
    }
  }

  _handleKeyDown(event) {
    this._executeOverride('handleKeyDown', arguments);

    if (this.icon && event.code === KeliInterface.Hotkeys.field.iconAction.code) {
      this.dispatchEvent(new CustomEvent('keli-input-icon-activated', {detail: {originalEvent: event}}));
      event.preventDefault();
      event.stopPropagation();
    }
  }

  _handleKeyPress() {
    this._executeOverride('handleKeyPress', arguments);
  }

  _handleDoubleClick() {
    this.dispatchEvent(new CustomEvent('keliDoubleClick', {detail: {element: this}}));
  }

  _maskedChanged(newValue) {
    this.classList.toggle('masked', newValue);
  }

  _clientWidthChanged(newValue) {
    if (this.showOverflowTooltip) {
      const tooltip = this.getTooltipInstance(this);
      tooltip && tooltip.setProps({maxWidth: newValue});
    }
  }

  _activeChanged(newValue) {
    this.classList.toggle('active', newValue);
  }

  _focusedChanged(newValue) {
    this.classList.toggle('focused', newValue);
    if (newValue) {
      if (!this.preventPanelEvents) {
        this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
          bubbles: true,
          composed: true,
          detail: {element: this}
        }));
      }
    }
  }

  _backgroundColorChanged(newValue) {
    const backgroundColor = backgroundColorValue(newValue);
    this.updateStyles({
      '--keli-input-field-background-color': backgroundColor,
      '--keli-input-field-text-color': foregroundColorValue(this.color, null, true, backgroundColor) || desiredTextColor(backgroundColor)
    });
  }

  _colorChanged(newValue) {
    this.updateStyles({'--keli-input-field-text-color': foregroundColorValue(newValue, this)});
  }

  _displayValueChanged(newValue) {
    const response = this._executeOverride('displayValueChanged', arguments);
    if (response === false) {
      return;
    }
    if (typeof response !== 'boolean') {
      newValue = response;
    }

    // If there are changes pending from the user, don't override them.
    if (this.fireKeliAccepted) {
      return;
    }

    if (this.$.input && !this.inputFormatRequired) {
      this.$.input.value = newValue;
    }
    this.currentValue = newValue;
    this.fieldInputOldValue = newValue;
    afterNextRender(this, () => {
      if (this.focused && !this.disabled) {
        this._focusInputAndSelect();
      }
    });
  }

  _maxLengthChanged(newValue) {
    if (!this._executeOverride('maxLengthChanged', arguments)) {
      return;
    }
    if (!this.$.input) {
      return;
    }
    if (Number.isInteger(newValue)) {
      this.$.input.setAttribute('maxlength', newValue);
    } else {
      this.$.input.removeAttribute('maxlength');
    }
  }

  _focusElementChanged(newValue) {
    if (!newValue || KeliInterface.isTouchDevice) {
      return;
    }

    afterNextRender(this, () => {
      this.dispatchEvent(new CustomEvent('focusElement', {detail: {element: this}}));
    });
  }

  _readOnlyChanged(newValue) {
    if (!this._executeOverride('readOnlyChanged', arguments)) {
      return;
    }
    this.disabled = !!newValue;
    if (newValue) {
      this.focused = false;

      if (!this.preventPanelEvents) {
        this.dispatchEvent(new CustomEvent('panelElementAttributeChanged', {
          bubbles: true, composed: true,
          detail: {element: this, attribute: 'readOnly'}
        }));
      }
      if (this.sendEditRowEvents) {
        this.dispatchEvent(new CustomEvent('editRowElementAttributeChanged', {
          bubbles: true, composed: true,
          detail: {element: this, attribute: 'readOnly'}
        }));
      }
    }
  }

  _textCasingChanged(newValue) {
    if (!this._executeOverride('textCasingChanged', arguments)) {
      return;
    }
    const property = newValue && newValue.toLowerCase();
    if (property && ['uppercase', 'lowercase', 'capitalize'].includes(property)) {
      this.$.input.style.textTransform = property;
    } else {
      this.$.input.style.textTransform = '';
    }
  }

  _currentValueChanged(newValue) {
    // Hack to get the tooltip information to calculate properly.
    if (this.$.input && !this._currentValueInitialized) {
      afterNextRender(this.$.input, () => {
        this._currentValueInitialized = true;
      });
    }

    if (!this._executeOverride('currentValueChanged', arguments)) {
      return;
    }

    if (!this.inputFormatRequired && this.$.input && newValue !== this.$.input.value) {
      this.$.input.value = newValue;
    }
    this.active = (newValue != null && `${newValue}`.length > 0);
    this.currentValueLength = newValue && newValue.length || 0;
    if (!this.validateOnBlur) {
      this._validate();
    }
  }

  _disabledChanged(newValue) {
    if (!this._executeOverride('disabledChanged', arguments)) {
      return;
    }
    if (this.$.input) {
      this.$.input.readOnly = newValue;
      if (newValue) {
        this.$.input.setAttribute('disabled', '');
      } else {
        this.$.input.removeAttribute('disabled');
      }
    }
    this.classList.toggle('disabled', newValue);
  }

  _validityStateChanged(newValue) {
    if (newValue && newValue.valid === false) {
      this.errorMessage = this.$.input.validationMessage || newValue.message || this.defaultErrorMessage;
      this.invalid = true;
    } else {
      this.invalid = false;
    }
  }

  _helpTextChanged(newValue) {
    if (!this._executeOverride('helpTextChanged', arguments)) {
      return;
    }

    if (this.$.help) {
      const tooltip = this.getTooltipInstance(this.$.help);
      if (tooltip) {
        if (newValue) {
          tooltip.enable();
        } else {
          tooltip.disable();
        }
      } else if (newValue) {
        this.addTooltip(this.$.help, 'helpText', {
          triggerTarget: this.$.help,
          placement: 'top-end',
          offset: [0, 0]
        });
      }
    }
  }

  _hotkeyChanged(newValue, oldValue) {
    if (!KeliInterface.Hotkeys.alphaNumerics.includes(newValue) && newValue !== true) {
      if (newValue) {
        if (!this.preventPanelEvents) {
          this.dispatchEvent(new CustomEvent('registerPanelHotkey', {
            bubbles: true,
            composed: true,
            detail: {
              'keys': newValue, 'scope': this, action: event => {
                this.activateShortcutKey(event);
              }
            }
          }));
        }
        this.shortcutKey = newValue;
      } else {
        if (!this.preventPanelEvents) {
          this.dispatchEvent(new CustomEvent('unregisterPanelHotkey', {
            bubbles: true,
            composed: true,
            detail: {'keys': oldValue}
          }));
        }
        this.shortcutKey = undefined;
      }
    } else {
      if (!this.preventPanelEvents) {
        this.dispatchEvent(new CustomEvent('panelElementAttributeChanged', {
          bubbles: true, composed: true,
          detail: {element: this, attribute: 'hotkey'}
        }));
      }
    }
  }

  _skipChanged(newValue) {
    if (this.focused && newValue === true) {
      if (!this.preventPanelEvents) {
        this.dispatchEvent(new CustomEvent('panelElementAttributeChanged', {
          bubbles: true, composed: true,
          detail: {element: this, attribute: 'skip'}
        }));
      }
    }
  }

  activateShortcutKey(event) {
    this._focusInput(event);
  }

  // Custom functions
  _validate() {
    if (!this._executeOverride('validate', arguments)) {
      return;
    }
    if (this.$.input && this.$.input.validity && this.$.input.validity.valid === false) {
      const validationMessage = this.$.input.validationMessage || this.errorMessage || this.defaultErrorMessage;
      this.validityState = {valid: false, message: validationMessage, fromUI: true};
    } else if (this.validityState && this.validityState.fromUI) {
      this.validityState = {valid: true};
    }
  }

  _focusInput(event) {
    if (!this._executeOverride('focusInput', arguments)) {
      return;
    }
    if (this.focused || this.disabled) {
      return;
    }
    this._focusInputAndSelect(event);
  }

  _focusInputAndSelect(event) {
    this.focused = true;
    if (event) {
      this.dispatchEvent(new CustomEvent('keliFocus', {
        bubbles: true,
        composed: true,
        detail: {element: this, event}
      }));
    }
    if (this.$.input) {
      this.$.input.focus();
      if (this.$.input.focusElement) {
        this.$.input.focusElement.focus();
      }
      if (this.selectOnFocus && this.$.input.select) {
        this.$.input.select();
      }
    }
  }

  resetToDefaultValue(value = '', runBlurAndFocus = false) {
    if (this.fireKeliAccepted) {
      if (runBlurAndFocus && this.handleBlur) {
        this.handleBlur();
      }
      this._keliAccept().then(() => {
        this.displayValue = value;

        if (runBlurAndFocus && this.handleFocus) {
          this.handleFocus();
        }
      });
    } else {
      this.displayValue = value;
    }
  }

  toRawFieldValue(value) {
    if (!value) {
      return '';
    }
    return value;
  }

  get fieldValueHasChanged() {
    let displayValue = this.displayValue;
    let currentValue = this.currentValue;

    // Support controls with custom parsing rules
    if (this.willParseTo) {
      currentValue = this.$.input.value;
    }

    // Support control specific value comparison
    if (this.toRawFieldValue) {
      displayValue = this.toRawFieldValue(displayValue);
      currentValue = this.toRawFieldValue(currentValue);
    }

    return (displayValue !== currentValue);
  }

  async _keliAccept(event, isClosingPanel) {
    if (this.preventKeliAcceptOnChangeThenRevert && !this.fieldValueHasChanged) {
      this.fireKeliAccepted = false;
      return;
    }

    if (!this._executeOverride('keliAccept', arguments)) {
      return;
    }

    if (this.fieldValueHasChanged) {
      this.displayValue = this.toRawFieldValue?.(this.currentValue) ?? this.currentValue;
    }

    const rowId = this.getKeliScopeRoot?.().getKeliRowId() || null;
    const detail = {
      element: this,
      event,
      value: this.displayValue,
      rowid: rowId,
      mergeBaseEventProperties: this._keliAcceptEventOverrides
    };

    if (this.fireKeliAccepted) {
      this.dispatchEvent(new CustomEvent('keliAccepted', {bubbles: true, composed: true, detail}));
      this.fireKeliAccepted = false;
    }
  }

  _executeOverride(functionName, providedArguments = []) {
    if (this[functionName]) {
      let response = this[functionName](...providedArguments);
      if (typeof response === 'undefined') {
        response = true;
      }
      return response;
    }
    return true;
  }
});